import {
  ActiveElement,
  Chart as ChartJS,
  ChartEvent,
  CategoryScale,
  Legend,
  LinearScale,
  BarElement,
  Title,
  Tooltip
} from 'chart.js';
import { useEffect, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import { Avatar, Modal } from '@components';
import { BarChartProps, BarChartStudentDetail, BarChartType } from '@types';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const PREVIEW_STUDENT_COUNT = 5;

const BarChart = ({
  type = BarChartType.BASIC,
  labels,
  primaryValue,
  secondaryValue,
  backgroundColor,
  showDataLabel = false,
  isHorizontal = false,
  barThickness,
  dataLabelFontType = 'desktop',
  studentDetails
}: BarChartProps) => {
  const [selectedStudents, setSelectedStudents] =
    useState<BarChartStudentDetail | null>(null);

  const tooltipIdRef = useRef(
    `bar-chart-student-tooltip-${Date.now()}-${Math.random()}`
  );

  const hideTooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const hasStudentDetails = Boolean(studentDetails?.length);

  const clearHideTooltipTimeout = () => {
    if (hideTooltipTimeoutRef.current) {
      clearTimeout(hideTooltipTimeoutRef.current);
      hideTooltipTimeoutRef.current = null;
    }
  };

  const removeStudentTooltip = () => {
    clearHideTooltipTimeout();

    const tooltipElement = document.getElementById(tooltipIdRef.current);

    if (tooltipElement) {
      tooltipElement.remove();
    }
  };

  const scheduleRemoveStudentTooltip = () => {
    clearHideTooltipTimeout();

    hideTooltipTimeoutRef.current = setTimeout(() => {
      const tooltipElement = document.getElementById(tooltipIdRef.current);

      if (tooltipElement) {
        tooltipElement.remove();
      }
    }, 300);
  };

  useEffect(
    () => () => {
      clearHideTooltipTimeout();

      const tooltipElement = document.getElementById(tooltipIdRef.current);

      if (tooltipElement) {
        tooltipElement.remove();
      }
    },
    []
  );

  const showStudentTooltip = (context: any) => {
    const tooltipModel = context.tooltip;

    if (!tooltipModel || tooltipModel.opacity === 0) {
      scheduleRemoveStudentTooltip();
      return;
    }

    const tooltipItems = tooltipModel.dataPoints;

    if (!tooltipItems?.length) {
      scheduleRemoveStudentTooltip();
      return;
    }

    const index = tooltipItems[0].dataIndex;
    const details = studentDetails?.[index];

    if (!details) {
      scheduleRemoveStudentTooltip();
      return;
    }

    clearHideTooltipTimeout();

    let tooltipElement = document.getElementById(tooltipIdRef.current);

    if (!tooltipElement) {
      tooltipElement = document.createElement('div');
      tooltipElement.id = tooltipIdRef.current;

      tooltipElement.className =
        'fixed z-[100] min-w-[220px] max-w-[280px] rounded-xl border border-[#e5f1f4] bg-white p-3 shadow-lg';

      tooltipElement.addEventListener('mouseenter', () => {
        clearHideTooltipTimeout();
      });

      tooltipElement.addEventListener('mouseleave', () => {
        scheduleRemoveStudentTooltip();
      });

      document.body.appendChild(tooltipElement);
    }

    tooltipElement.innerHTML = '';

    const titleElement = document.createElement('div');
    titleElement.className = 'mb-2 text-xs font-bold text-[#294f61]';

    titleElement.textContent = details.label;

    tooltipElement?.appendChild(titleElement);

    const countElement = document.createElement('div');
    countElement.className = 'mb-2 text-[10px] font-medium text-grey5';

    countElement.textContent = `Students (${details.students.length})`;

    tooltipElement?.appendChild(countElement);

    const previewStudents = details.students.slice(0, PREVIEW_STUDENT_COUNT);

    previewStudents.forEach((student) => {
      const studentElement = document.createElement('div');

      studentElement.className =
        'mb-1 truncate text-[10px] font-medium text-[#294f61]';

      studentElement.textContent = `• ${student.name}`;

      tooltipElement?.appendChild(studentElement);
    });

    const remainingCount = details.students.length - previewStudents.length;

    if (remainingCount > 0) {
      const remainingElement = document.createElement('div');

      remainingElement.className = 'mt-1 text-[10px] font-medium text-grey5';

      remainingElement.textContent = `+ ${remainingCount} more`;

      tooltipElement.appendChild(remainingElement);
    }

    if (details.students.length > PREVIEW_STUDENT_COUNT) {
      const viewMoreButton = document.createElement('button');

      viewMoreButton.type = 'button';

      viewMoreButton.className =
        'mt-2 text-[10px] font-semibold text-primary underline';

      viewMoreButton.textContent = 'View more';

      viewMoreButton.addEventListener('click', () => {
        setSelectedStudents(details);
        removeStudentTooltip();
      });

      tooltipElement.appendChild(viewMoreButton);
    }

    const position = context.chart.canvas.getBoundingClientRect();

    tooltipElement.style.left = `${position.left + tooltipModel.caretX}px`;

    tooltipElement.style.top = `${
      position.top + tooltipModel.caretY - tooltipElement.offsetHeight - 10
    }px`;

    const tooltipRect = tooltipElement.getBoundingClientRect();

    if (tooltipRect.right > window.innerWidth) {
      tooltipElement.style.left = `${
        window.innerWidth - tooltipRect.width - 10
      }px`;
    }

    if (tooltipRect.left < 10) {
      tooltipElement.style.left = '10px';
    }

    if (tooltipRect.top < 10) {
      tooltipElement.style.top = `${position.top + tooltipModel.caretY + 15}px`;
    }
  };

  const handleBarClick = (_event: ChartEvent, elements: ActiveElement[]) => {
    if (!hasStudentDetails || elements.length === 0) {
      return;
    }

    const { index } = elements[0];
    const details = studentDetails?.[index];

    if (details?.students?.length) {
      setSelectedStudents(details);
      removeStudentTooltip();
    }
  };

  const dataLabelFontSize = {
    mobile: 14,
    desktop: 25
  };

  const options = {
    indexAxis: isHorizontal ? ('y' as const) : ('x' as const),

    responsive: true,

    animation: {
      duration: 0
    },

    maintainAspectRatio: false,

    onClick: handleBarClick,

    onHover: (event: ChartEvent, elements: ActiveElement[]) => {
      if (!hasStudentDetails) {
        return;
      }

      const target = event.native?.target as HTMLElement | null;

      if (target) {
        target.style.cursor = elements.length > 0 ? 'pointer' : 'default';
      }
    },

    plugins: {
      datalabels: {
        display: showDataLabel,
        anchor: 'start' as const,
        align: 'top' as const,
        textAlign: 'center' as const,
        font: {
          size: dataLabelFontSize[dataLabelFontType],
          weight: 800
        },
        labels: {
          value: {
            color: 'black'
          }
        },
        formatter(val: any) {
          return `${val}%`;
        }
      },

      legend: {
        display: false
      },

      title: {
        display: false
      },

      tooltip: {
        /*
         * Existing behavior is preserved when studentDetails
         * are not supplied.
         */
        enabled: !hasStudentDetails,

        /*
         * Student-enabled charts use a custom HTML tooltip.
         */
        external: hasStudentDetails ? showStudentTooltip : undefined,

        color: 'black',
        backgroundColor: 'white',
        titleColor: 'black',
        bodyColor: 'black',
        padding: 10,
        borderWidth: 0.5,
        borderColor: 'black',

        callbacks: {
          label(context: any) {
            let label = context.dataset.label || '';

            if (label) {
              label += ': ';
            }

            if (isHorizontal) {
              if (context.parsed.x !== null) {
                label += `${context.parsed.x}`;
              }
            } else if (context.parsed.y !== null) {
              label += `${context.parsed.y}`;
            }

            return label;
          }
        }
      }
    },

    scales: {
      x: {
        stacked: !!BarChartType.STACKED,
        grid: {
          display: false
        },
        border: {
          display: false
        }
      },

      y: {
        stacked: !!BarChartType.STACKED,
        grid: {
          display: false
        },
        border: {
          display: false
        },

        ...(isHorizontal && {
          ticks: {
            callback(value: string | number, index: number) {
              const label = labels[index];

              if (label?.length > 35) {
                return `${label.slice(0, 30).trim()}...`;
              }

              return label;
            }
          }
        })
      }
    }
  };

  const basicData = {
    labels,
    datasets: [
      {
        data: primaryValue,
        backgroundColor,
        borderSkipped: false,
        borderRadius: 16,
        barThickness
      }
    ]
  };

  const stackData = {
    labels,
    datasets: [
      {
        data: primaryValue,
        backgroundColor: backgroundColor[0],
        borderRadius: 16,
        barThickness
      },
      {
        data: secondaryValue,
        backgroundColor: backgroundColor[1],
        borderRadius: 16,
        barThickness
      }
    ]
  };

  return (
    <>
      {type === BarChartType.BASIC && (
        <Bar options={options} data={basicData} />
      )}

      {type === BarChartType.STACKED && (
        <Bar options={options} data={stackData} />
      )}

      {selectedStudents && (
        <Modal
          isOpen={Boolean(selectedStudents)}
          onCancel={() => setSelectedStudents(null)}
          modalHeader={`Students - ${selectedStudents.label}`}
          isCloseIconRequired
          className="w-full max-w-md"
          headerClassName="px-6 pt-5 text-lg font-bold text-grey1"
          modalBodyClassName="px-6 pb-6 pt-4"
        >
          <div className="max-h-[60vh] overflow-y-auto">
            <div className="space-y-1">
              {selectedStudents.students.map((student) => (
                <div
                  key={student.id}
                  className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-[#f5fbfd]"
                >
                  <Avatar
                    name={student.name}
                    className="size-8 shrink-0 rounded-full text-xs text-white"
                    bgColor="#7C57FF"
                    imageUrl={student.avatarUrl || ''}
                  />

                  <span
                    className="truncate text-sm font-medium text-[#294f61]"
                    title={student.name}
                  >
                    {student.name}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-4 text-xs text-grey5">
            {selectedStudents.students.length} students
          </div>
        </Modal>
      )}
    </>
  );
};

export default BarChart;
